"use client"

import { t } from "@/languages/i18n"
import { DestinationItem, TourItem } from "@/types/types"
import { EditOutlined } from "@ant-design/icons"
import { Button, Flex, Form, Input, message, Select, Spin } from "antd"
import Title from "antd/es/typography/Title"
import { useRouter, useSearchParams } from "next/navigation"
import React, { useEffect, useState } from "react"
import { fetchAllTourV2s } from "../../tour/services/tours"
import { fetchDestinationById, updateDestination } from "../services/destination"

const FormEditDestination: React.FC = () => {
  const [form] = Form.useForm()
  const router = useRouter()
  const searchParams = useSearchParams()
  const destinationId = searchParams.get("id")

  const [tours, setTours] = useState<TourItem[]>([])
  const [toursLoading, setToursLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [destination, setDestination] = useState<DestinationItem | null>(null)

  useEffect(() => {
    if (destinationId) {
      loadDestination()
      loadTours()
    }
  }, [destinationId])

  const loadDestination = async () => {
    try {
      if (destinationId) {
        const response = await fetchDestinationById(destinationId)
        const data = (response as { data: DestinationItem }).data
        setDestination(data)

        form.setFieldsValue({
          name: data.attributes.name,
          tourIds: data.attributes.tour_v_2s?.data?.map(tour => tour.id) || [],
        })
      }
    } catch (error) {
      console.error("Error loading destination:", error)
      message.error(t("Error loading destination!"))
    }
  }

  const loadTours = async () => {
    setToursLoading(true)
    try {
      const response = await fetchAllTourV2s()
      const data = (response as { data: TourItem[] }).data
      setTours(data || [])
    } catch (error) {
      console.error("Error loading tours:", error)
      message.error(t("Error loading tours!"))
    } finally {
      setToursLoading(false)
    }
  }

  const onFinish = async (values: { name: string; tourIds?: number[] }) => {
    try {
      setLoading(true)
      await updateDestination(Number(destinationId), values)
      message.success(t("Destination updated successfully!"))
      router.push("/admin/destinations")
    } catch (error) {
      console.error("Error updating destination:", error)
      message.error(t("Error updating destination!"))
    } finally {
      setLoading(false)
    }
  }

  const onCancel = () => {
    router.push("/admin/destinations")
  }

  if (!destination) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <Title level={2}>{t("Edit Destination")}</Title>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <Form form={form} layout="vertical" onFinish={onFinish} className="max-w-2xl">
          <Form.Item
            name="name"
            label={t("Destination Name")}
            rules={[
              { required: true, message: t("Please enter destination name!") },
              { min: 2, message: t("Destination name must be at least 2 characters!") },
              { max: 100, message: t("Destination name cannot exceed 100 characters!") },
            ]}
          >
            <Input placeholder={t("Enter destination name...")} size="large" />
          </Form.Item>

          <Form.Item name="tourIds" label={t("Select Tours")} help={t("Select tours for this destination")}>
            {toursLoading ? (
              <div className="flex justify-center py-4">
                <Spin />
              </div>
            ) : (
              <Select
                mode="multiple"
                placeholder={t("Select tours...")}
                size="large"
                showSearch
                filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                options={tours.map(tour => ({
                  value: tour.id,
                  label: tour.attributes?.name || tour.attributes?.title || t("No tours found"),
                }))}
              />
            )}
          </Form.Item>

          <Form.Item className="mb-0">
            <Flex gap="middle" justify="end">
              <Button onClick={onCancel} size="large">
                {t("Back")}
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                icon={<EditOutlined />}
                className="bg-green-600 hover:bg-green-700"
              >
                {t("Update Destination")}
              </Button>
            </Flex>
          </Form.Item>
        </Form>
      </div>
    </div>
  )
}

export default FormEditDestination
